"use client";

import { useEffect } from "react";

/**
 * Lock page scrolling while an overlay is open.
 *
 * ── WHY THIS EXISTS (it is a bug fix, not only a tidy-up) ────────────────────
 * The same ~12-line effect was pasted at five sites — FeaturedGallery, the
 * storefront GalleryLightbox, the admin GalleryClient, ProductDetail and
 * `components/ui/sidebar.tsx` — and the sidebar copy had DIVERGED: it set
 * `overflow: hidden` on both elements but never set `data-lightbox-open`.
 *
 * That attribute is not decoration. `app/globals.css` keys two rules off it —
 * `html:has(body[data-lightbox-open]) { scrollbar-gutter: auto }` and
 * `body[data-lightbox-open] { padding-right/margin-right: 0 !important }` —
 * which remove the reserved scrollbar gutter that would otherwise show as a
 * white strip down the side of a full-bleed overlay. Two components also READ
 * it as a behavioural contract: `app/(main)/products/ProductsClient.tsx`
 * swallows a pointer-down-outside and `components/products/detail/ProductDetailDrawer`
 * swallows an Escape while it is present, so a lightbox close does not also
 * dismiss the modal underneath it. A copy that locks scroll without setting the
 * flag gets the gutter strip and forfeits both guards.
 *
 * ── WHY IT REFERENCE-COUNTS ──────────────────────────────────────────────────
 * None of the five copies did, and each saved the PREVIOUS `overflow` into its
 * own closure. Nest two locks and the inner one saves `"hidden"` (already set by
 * the outer), then "restores" `"hidden"` when it closes — leaving the page
 * permanently unscrollable with no overlay on screen. That is reachable today:
 * ProductDetail's lightbox opens inside ProductDetailDrawer, and the admin
 * sidebar's mobile drawer can be open when a dialog locks scroll. The counter
 * lives at module scope, so only the FIRST lock records the pre-lock values and
 * only the LAST release restores them.
 *
 * A module-level counter is correct here precisely because the thing it guards —
 * `<html>`/`<body>` — is also a single global. It is not application state, so
 * it does not belong in a store (`.claude/rules/state-management.md`).
 */

/** How many active locks are currently held. */
let lockCount = 0;
/** The pre-lock inline values, captured once when the count goes 0 → 1. */
let savedHtmlOverflow = "";
let savedBodyOverflow = "";

function acquire(): void {
  lockCount += 1;
  if (lockCount > 1) return;
  savedHtmlOverflow = document.documentElement.style.overflow;
  savedBodyOverflow = document.body.style.overflow;
  document.documentElement.style.overflow = "hidden";
  document.body.style.overflow = "hidden";
  document.body.setAttribute("data-lightbox-open", "");
}

function release(): void {
  lockCount = Math.max(0, lockCount - 1);
  if (lockCount > 0) return;
  document.documentElement.style.overflow = savedHtmlOverflow;
  document.body.style.overflow = savedBodyOverflow;
  document.body.removeAttribute("data-lightbox-open");
}

/**
 * While `active` is true, `<html>` and `<body>` are `overflow: hidden` and
 * `<body>` carries `data-lightbox-open`. Both are undone when `active` goes
 * false or the component unmounts.
 *
 * Safe to call unconditionally with a boolean — that is the point, since the
 * hook rules forbid calling it inside an `if`.
 */
export function useScrollLock(active: boolean): void {
  useEffect(() => {
    if (!active) return;
    acquire();
    return release;
  }, [active]);
}

/**
 * Test-only reset of the module counter.
 *
 * The counter is module state, so it survives `cleanup()` between tests in the
 * same file: one spec that unmounts without releasing would leak a lock into
 * the next and make it silently pass. Never call this from app code.
 */
export function __resetScrollLockForTests(): void {
  lockCount = 0;
  savedHtmlOverflow = "";
  savedBodyOverflow = "";
}
